import { ENV } from '@app/constants/env';
import React, { Suspense, createContext, useContext, useEffect, useRef, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Link,
  ListItemButton,
  ListItemText,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { ExpandLess, ExpandMore } from "@mui/icons-material";
import { Paper, Popper, useMediaQuery } from "@mui/material";
import { ThemeProvider } from "@mui/material/styles";
import { ListItemIconButton } from "@sdk-react-ui-molecules/appbar/ListItemIconButton";
import { useSidebarExpandState } from "@sdk-react-ui-molecules/appbar/useSidebarState";
import { AppBar } from "@sdk-react-ui-molecules/appbar/AppBar";
import { Sidebar } from "@sdk-react-ui-molecules/appbar/Sidebar";
import { UserMenu } from "@sdk-react-ui-molecules/appbar/UserMenu";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router";
import { LinkRef } from "../atoms/LinkRef";
import { useGetIdentity, useLogout } from "@sdk-react-identity/core/hooks";
import { authProvider } from "../../helpers/authProvider";
import { Logout } from "@sdk-react-ui-molecules/appbar/Logout";
import { Login } from "@sdk-react-ui-molecules/appbar/Login";
import { SidebarToggleButton } from "@sdk-react-ui-molecules/appbar/SidebarToggleButton";
import { Logo } from "@sdk-react-ui-atoms/theme/logo";
import { AppName } from "@sdk-react-ui-molecules/appbar/AppName";
import { Menu } from "@sdk-react-ui-molecules/appbar/Menu";
import { MenuItemLink } from "@sdk-react-ui-molecules/appbar/MenuItemLink";
import {
  IconAddFilled,
  IconBookingAdd,
  IconBookingList,
  IconCalendarTodayFilled,
  IconChevronRightFilled,
  IconClaims,
  IconCreditCardFilled,
  IconInternalManagement,
  IconLocationOnFilled,
  IconReportFilled,
  IconSettingsFilled,
  IconSortList,
  IconSpaceDashboardFilled,
  IconTrainFilled,
  IconUserManagement,
  IconWarehouseFilled,
  IconWarehouseOutlined,
  IconWorkFilled,
  IconWorkOrder,
  IconInvoiceOutlined,
  IconSummary,
  IconCurrencyExchange,
  IconAnalytics,
  IconRoadFilled,
  IconPlaylistAddOutlined,
} from "@sdk-react-ui-atoms/icons";
import { useGetUserShowsForDDLQuery } from "@app/api/common.ddl.api";
import {
  useGetSelectedShowIdQuery,
  useSetSelectedShowIdMutation,
} from "@app/api/showSelectionSlice";

// Label for menu items that open a sub menu: text with a down arrow (up arrow when open)
const SubMenuLabel = ({ text, open }: { text: string; open: boolean }) => (
  <>
    <ListItemText>{text}</ListItemText>
    {open ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
  </>
);

// true while rendering inside a collapsed-sidebar flyout, so nested sub menus expand inline there
const FlyoutContext = createContext(false);

// Inside the flyout every MenuItemLink renders with its text, even though the sidebar is collapsed
const forceExpandMenuItems = (outer: any) => ({
  ...outer,
  components: {
    ...outer.components,
    SdkMenuItemLink: {
      ...outer.components?.SdkMenuItemLink,
      defaultProps: { ...outer.components?.SdkMenuItemLink?.defaultProps, forceExpand: true },
    },
  },
});

type SubMenuProps = {
  text: string;
  leftIcon: React.ReactElement;
  open: boolean;
  onToggle: () => void;
  style?: React.CSSProperties;
  children: React.ReactNode;
};

// Menu item with a sub menu.
// Expanded sidebar: toggles the children inline below it.
// Collapsed sidebar (icons only): shows the children in a flyout on hover or click.
const SubMenu = ({ text, leftIcon, open, onToggle, style, children }: SubMenuProps) => {
  const [expand] = useSidebarExpandState();
  const isSmall = useMediaQuery((theme: any) => theme.breakpoints.down("md"));
  const inFlyout = useContext(FlyoutContext);
  const location = useLocation();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  // close the flyout after navigating to a page from it
  useEffect(() => setAnchorEl(null), [location.pathname]);
  useEffect(() => () => clearTimeout(closeTimer.current), []);

  if (expand || isSmall || inFlyout) {
    return (
      <>
        <MenuItemLink
          to="#"
          primaryText={text}
          children={<SubMenuLabel text={text} open={open} />}
          leftIcon={leftIcon}
          style={style}
          onClick={(e) => {
            e.preventDefault();
            onToggle();
          }}
        />
        {open &&
          (inFlyout ? (
            // indent nested items inside the flyout (&& raises specificity over the flyout rule)
            <Box sx={{ "&& .MuiListItemButton-root": { paddingLeft: "40px !important" } }}>
              {children}
            </Box>
          ) : (
            children
          ))}
      </>
    );
  }

  const openFlyout = (target: HTMLElement) => {
    clearTimeout(closeTimer.current);
    setAnchorEl(target);
  };
  const closeFlyout = () => {
    clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setAnchorEl(null), 150);
  };

  return (
    // the Popper is a React child, so moving the mouse into it does not trigger mouse leave
    <Box
      onMouseEnter={(e) => openFlyout(e.currentTarget)}
      onMouseLeave={closeFlyout}
    >
      <ListItemIconButton
        component={NavLink as any}
        to="#"
        aria-label={text}
        aria-haspopup="true"
        aria-expanded={Boolean(anchorEl)}
        className={anchorEl ? "Mui-selected" : undefined}
        onClick={(e: React.MouseEvent<HTMLElement>) => {
          e.preventDefault();
          if (anchorEl) setAnchorEl(null);
          else openFlyout(e.currentTarget.parentElement as HTMLElement);
        }}
      >
        {leftIcon}
      </ListItemIconButton>
      <Popper
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        placement="right-start"
        sx={{ zIndex: (theme) => theme.zIndex.modal }}
      >
        <Paper
          elevation={6}
          sx={{
            minWidth: 240,
            maxHeight: "80vh",
            overflowY: "auto",
            py: 0.5,
            "& .MuiListItemButton-root": { paddingLeft: "16px !important" },
          }}
        >
          <Typography variant="subtitle2" sx={{ px: 2, py: 1, color: "text.secondary" }}>
            {text}
          </Typography>
          <ThemeProvider theme={forceExpandMenuItems}>
            <FlyoutContext.Provider value={true}>{children}</FlyoutContext.Provider>
          </ThemeProvider>
        </Paper>
      </Popper>
    </Box>
  );
};

const MyMenu = () => {
  const [openJobSubMenu, setOpenJobSubMenu] = useState(false);
  const [openServiceSubMenu, setServiceSubMenu] = useState(false);
  const [openTrafficServiceSubMenu, setTrafficServiceSubMenu] = useState(false);
  const [openadminSubMenu, setadminSubMenu] = useState(false);
  const [companySubMenuOpen, setCompanySubMenuOpen] = useState(false);
  const [openFinanceSubMenu, setOpenFinanceSubMenu] = useState(false);
  const [openFinanceReportsSubMenu, setOpenFinanceReportsSubMenu] = useState(false);
  const [openPaymentSubMenu, setOpenPaymentSubMenu] = useState(false);
  const [openCustomsSubMenu, setOpenCustomsSubMenu] = useState(false);
  const [openFormTemplateSubMenu, setOpenFormTemplateSubMenu] = useState(false);

  const toggleJobSubMenu = () => setOpenJobSubMenu(!openJobSubMenu);
  const toggleServiceSubMenu = () => setServiceSubMenu(!openServiceSubMenu);
  const toggleTrafficServiceSubMenu = () =>
    setTrafficServiceSubMenu(!openTrafficServiceSubMenu);
  const toggleadminSubMenu = () => setadminSubMenu(!openadminSubMenu);
  const toggleFinanceSubMenu = () => setOpenFinanceSubMenu(!openFinanceSubMenu);
  const togglePaymentSubMenu = () => setOpenPaymentSubMenu(!openPaymentSubMenu);
  const toggleCustomsSubMenu = () => setOpenCustomsSubMenu(!openCustomsSubMenu);
  const toggleFormTemplateSubMenu = () =>
    setOpenFormTemplateSubMenu(!openFormTemplateSubMenu);
  const userData = JSON.parse(localStorage.getItem("userData") || "{}");

  const navigate = useNavigate();
  const handleCreatejob = () => {
    localStorage.setItem("submissionLogId", "0");
    localStorage.setItem("linkedExhibitorId", "0");
    localStorage.setItem("linkedShowId", "0");
    localStorage.setItem("hallNumber", "0");
    localStorage.setItem("standNumber", "0");
    navigate("/joblist/createjob");
  };
  return (
    <Menu>
      <MenuItemLink
        to={userData.dsvRole === "OnSiteUser" ? "/JobService" : "/Dashboard?flag=True"}
        primaryText="Dashboard"
        leftIcon={<IconSpaceDashboardFilled />}
      />

      {/* eFairs Job */}
      {userData?.isJobsVisible && (
        <>
          <SubMenu
            text="eFairs Job"
            leftIcon={<IconWorkFilled />}
            open={openJobSubMenu}
            onToggle={toggleJobSubMenu}
          >
              <MenuItemLink
                to="/joblist"
                primaryText="Job List"
                leftIcon={<IconBookingList />}
                style={{ paddingLeft: 32 }}
              />
              <MenuItemLink
                to="/joblist/createjob"
                onClick={handleCreatejob}

                primaryText="Create Job"
                leftIcon={<IconWorkFilled />}
                style={{ paddingLeft: 32 }}
              />
          </SubMenu>
        </>
      )}

      {/* Service Schedule/Manifest - conditional on role and visibility */}
      {["Global", "Region", "Country"].includes(userData.dsvRole) &&
        userData.isCountrysettingsVisible && (
          <>
            <SubMenu
              text="Service"
              leftIcon={<IconBookingList />}
              open={openServiceSubMenu}
              onToggle={toggleServiceSubMenu}
            >
                <MenuItemLink
                  to="/JobService"
                  primaryText="Service Schedule/Manifest"
                  leftIcon={<IconWorkOrder />}
                />
                <MenuItemLink
                  to="/JobService/SustainabilityReport"
                  primaryText="Sustainability Report"
                  leftIcon={<IconReportFilled />}
                />
            </SubMenu>
          </>
        )}

      {/* Admin menu with if / else if / else logic */}
      {(() => {
        if (["Global", "Region", "Country"].includes(userData.dsvRole)) {
          // Global/Region/Country
          return (
            <>
              <SubMenu
                text="Admin"
                leftIcon={<IconSettingsFilled />}
                open={openadminSubMenu}
                onToggle={toggleadminSubMenu}
              >
                  {userData?.isUserlistVisible && (
                    <MenuItemLink
                      to="/userlist"
                      primaryText="Users"
                      leftIcon={<IconUserManagement />}
                      style={{ paddingLeft: 32 }}
                    />
                  )}
                  {userData?.isShowVisible && (
                    <MenuItemLink
                      to="/showlist"
                      primaryText="Show List"
                      leftIcon={<IconBookingAdd />}
                      style={{ paddingLeft: 32 }}
                    />
                  )}
                  {userData?.isCustomerlistVisible && (
                    <MenuItemLink
                      to="/exhibitors"
                      primaryText="Exhibitor List"
                      leftIcon={<IconBookingAdd />}
                      style={{ paddingLeft: 32 }}
                    />
                  )}
                  {userData?.isOrganizerVisible && (
                    <MenuItemLink
                      to="/OrganizerList"
                      primaryText="Organizer"
                      leftIcon={<IconWarehouseFilled />}
                      style={{ paddingLeft: 32 }}
                    />
                  )}
                  {userData?.isVenueVisible && (
                    <MenuItemLink
                      to="/MstrVenue"
                      primaryText="Venue"
                      leftIcon={<IconWarehouseFilled />}
                      style={{ paddingLeft: 32 }}
                    />
                  )}
                  {userData?.isConsigneeVisible && (
                    <MenuItemLink
                      to="/MstrDSVOffice"
                      primaryText="DSV Office"
                      leftIcon={<IconWarehouseFilled />}
                      style={{ paddingLeft: 32 }}
                    />
                  )}
                  {userData?.isFinanceVisible && (
                    <MenuItemLink
                      to="/MdgList"
                      primaryText="MDG Data"
                      leftIcon={<IconWarehouseFilled />}
                      style={{ paddingLeft: 32 }}
                    />
                  )}
                  {userData?.isConsigneeVisible && (
                    <SubMenu
                      text="Master Data"
                      leftIcon={<IconAddFilled />}
                      style={{ paddingLeft: 32 }}
                      open={companySubMenuOpen}
                      onToggle={() => setCompanySubMenuOpen(!companySubMenuOpen)}
                    >
                      <MenuItemLink
                        to="/MstrCompany"
                        primaryText="Company List"
                        leftIcon={<IconWarehouseFilled />}
                        style={{ paddingLeft: 64 }}
                      />
                      <MenuItemLink
                        to="/MstrLocation"
                        primaryText="Location"
                        leftIcon={<IconLocationOnFilled />}
                        style={{ paddingLeft: 64 }}
                      />
                      <MenuItemLink
                        to="/MstrDepartment"
                        primaryText="Department"
                        leftIcon={<IconSpaceDashboardFilled />}
                        style={{ paddingLeft: 64 }}
                      />
                      <MenuItemLink
                        to="/MstrChargeCode"
                        primaryText="Charge Code"
                        leftIcon={<IconBookingAdd />}
                        style={{ paddingLeft: 64 }}
                      />
                      <MenuItemLink
                        to="/MstrShowPrefix"
                        primaryText="Show Prefix"
                        leftIcon={<IconWarehouseFilled />}
                        style={{ paddingLeft: 64 }}
                      />
                      <MenuItemLink
                        to="/MstrVatTax"
                        primaryText="Tax"
                        leftIcon={<IconTrainFilled />}
                        style={{ paddingLeft: 64 }}
                      />
                      <MenuItemLink
                        to="/MstrTransportCode"
                        primaryText="Transport Code"
                        leftIcon={<IconTrainFilled />}
                        style={{ paddingLeft: 64 }}
                      />
                    </SubMenu>
                  )}
              </SubMenu>
            </>
          );
        } else if (userData.dsvRole === "Agent") {
          // Agent
          return (
            <>
              <SubMenu
                text="Admin"
                leftIcon={<IconSettingsFilled />}
                open={openadminSubMenu}
                onToggle={toggleadminSubMenu}
              >
                  {userData?.isCustomerlistVisible && (
                    <MenuItemLink
                      to="/exhibitors"
                      primaryText="Exhibitor"
                      leftIcon={<IconBookingAdd />}
                      style={{ paddingLeft: 32 }}
                    />
                  )}
              </SubMenu>
            </>
          );
        } else {
          // Other roles — show Admin items based on visibility flags
          const anyVisible =
            userData?.isCustomerlistVisible ||
            userData?.isShowVisible ||
            userData?.isConsigneeVisible ||
            userData?.isCountrySettingsVisible ||
            userData?.isJobsVisible ||
            userData?.isUserlistVisible ||
            userData?.isWarehouseVisible ||
            userData?.isReportsVisible ||
            userData?.isOrganizerVisible ||
            userData?.isVenueVisible;

          if (!anyVisible) return null;

          return (
            <>
              <SubMenu
                text="Admin"
                leftIcon={<IconSettingsFilled />}
                open={openadminSubMenu}
                onToggle={toggleadminSubMenu}
              >
                  {userData?.isCustomerlistVisible && (
                    <MenuItemLink
                      to="/exhibitors"
                      primaryText="Exhibitor"
                      leftIcon={<IconBookingAdd />}
                      style={{ paddingLeft: 32 }}
                    />
                  )}
                  {userData?.isShowVisible && (
                    <MenuItemLink
                      to="/showlist"
                      primaryText="Shows"
                      leftIcon={<IconBookingAdd />}
                      style={{ paddingLeft: 32 }}
                    />
                  )}
                  {userData?.isConsigneeVisible && (
                    <MenuItemLink
                      to="/MstrDSVOffice"
                      primaryText="Consignee"
                      leftIcon={<IconWarehouseFilled />}
                      style={{ paddingLeft: 32 }}
                    />
                  )}
                  {userData?.isCountrySettingsVisible && (
                    <MenuItemLink
                      to="/MstrCountrySettings"
                      primaryText="Country Settings"
                      leftIcon={<IconSettingsFilled />}
                      style={{ paddingLeft: 32 }}
                    />
                  )}
                  {userData?.isUserlistVisible && (
                    <MenuItemLink
                      to="/userlist"
                      primaryText="User"
                      leftIcon={<IconUserManagement />}
                      style={{ paddingLeft: 32 }}
                    />
                  )}
                  {userData?.isWarehouseVisible && (
                    <MenuItemLink
                      to="/MstrWarehouse"
                      primaryText="Warehouse"
                      leftIcon={<IconWarehouseFilled />}
                      style={{ paddingLeft: 32 }}
                    />
                  )}
                  {userData?.isOrganizerVisible && (
                    <MenuItemLink
                      to="/OrganizerList"
                      primaryText="Organizer"
                      leftIcon={<IconWarehouseFilled />}
                      style={{ paddingLeft: 32 }}
                    />
                  )}
                  {userData?.isVenueVisible && (
                    <MenuItemLink
                      to="/MstrVenue"
                      primaryText="Venue"
                      leftIcon={<IconWarehouseFilled />}
                      style={{ paddingLeft: 32 }}
                    />
                  )}
              </SubMenu>
            </>
          );
        }
      })()}

      {/* Traffic */}
      {["Global", "Region", "Country"].includes(userData.dsvRole) &&
        userData?.isTrafficVisible && (
          <>
            <SubMenu
              text="Traffic"
              leftIcon={<IconRoadFilled />}
              open={openTrafficServiceSubMenu}
              onToggle={toggleTrafficServiceSubMenu}
            >
                {userData?.isTrafficCreateVisible && (
                  <MenuItemLink
                    to="/questionnaireform"
                    primaryText="Create Form"
                    leftIcon={<IconPlaylistAddOutlined />}
                    style={{ paddingLeft: 32 }}
                  />
                )}
                <MenuItemLink
                  to="/trfcjoblist"
                  primaryText="View Traffic Job"
                  leftIcon={<IconCalendarTodayFilled />}
                  style={{ paddingLeft: 32 }}
                />
                <MenuItemLink
                  to="/bookedslotsdetails"
                  primaryText="Traffic Schedule / Manifest"
                  leftIcon={<IconReportFilled />}
                  style={{ paddingLeft: 32 }}
                />
            </SubMenu>
          </>
        )}

      {/* Finance */}
      {userData?.isFinanceVisible && (
        <>
          <SubMenu
            text="Finance"
            leftIcon={<IconCurrencyExchange />}
            open={openFinanceSubMenu}
            onToggle={toggleFinanceSubMenu}
          >
              <MenuItemLink
                to="/ARTransactionsList"
                primaryText="Customer Invoice List"
                leftIcon={<IconInvoiceOutlined />}
                style={{ paddingLeft: 32 }}
              />
              <MenuItemLink
                to="/APTransactionsList"
                primaryText="Supplier Invoice List"
                leftIcon={<IconInvoiceOutlined />}
                style={{ paddingLeft: 32 }}
              />
              <MenuItemLink
                to="/ArInvoiceExtractList"
                primaryText="Approved Extract"
                leftIcon={<IconReportFilled />}
                style={{ paddingLeft: 32 }}
              />
              <MenuItemLink
                to="/APARAccuralExtractList"
                primaryText="Accruals Extract"
                leftIcon={<IconReportFilled />}
                style={{ paddingLeft: 32 }}
              />
              <MenuItemLink
                to="/PLReport"
                primaryText="Show Profitability Report"
                leftIcon={<IconAnalytics />}
                style={{ paddingLeft: 32 }}
              />
              
              <MenuItemLink
                to="/ARApprovedsapdirectextract"
                primaryText="SAP Direct Extract"
                leftIcon={<IconReportFilled />}
                style={{ paddingLeft: 32 }}
              />

              <MenuItemLink
                to="/InvoiceDocumentUpload"
                primaryText="Invoice Upload"
                leftIcon={<IconReportFilled />}
                style={{ paddingLeft: 32 }}
              />

              <SubMenu
                text="Reports"
                leftIcon={<IconReportFilled />}
                style={{ paddingLeft: 32 }}
                open={openFinanceReportsSubMenu}
                onToggle={() => setOpenFinanceReportsSubMenu(!openFinanceReportsSubMenu)}
              >
                  <MenuItemLink
                    to="/ARInvoiceRpt"
                    primaryText="AR Invoice Report"
                    leftIcon={<IconAnalytics />}
                    style={{ paddingLeft: 64 }}
                  />
                  <MenuItemLink
                    to="/SupplierInvoicesRpt"
                    primaryText="Supplier Invoices Report"
                    leftIcon={<IconAnalytics />}
                    style={{ paddingLeft: 64 }}
                  />
                  <MenuItemLink
                    to="/WIPSalesRpt"
                    primaryText="WIP Sales Report"
                    leftIcon={<IconAnalytics />}
                    style={{ paddingLeft: 64 }}
                  />
                  <MenuItemLink
                    to="/CostAaccrualrpt"
                    primaryText="Cost Accrual Report"
                    leftIcon={<IconAnalytics />}
                    style={{ paddingLeft: 64 }}
                  />
              </SubMenu>
          </SubMenu>
        </>
      )}

      {/* Payment */}
      {userData?.isPaymentVisible && (
        <>
          <SubMenu
            text="Payment"
            leftIcon={<IconCreditCardFilled />}
            open={openPaymentSubMenu}
            onToggle={togglePaymentSubMenu}
          >
              <MenuItemLink
                to="/transactionlist"
                primaryText="Transaction List"
                leftIcon={<IconCreditCardFilled />}
                style={{ paddingLeft: 32 }}
              />
              <MenuItemLink
                to="/paymentdetails"
                primaryText="Payment Details"
                leftIcon={<IconCreditCardFilled />}
                style={{ paddingLeft: 32 }}
              />
          </SubMenu>
        </>
      )}

      {/* Customs */}
      {userData?.isCustomVisible && (
        <>
          <SubMenu
            text="Customs"
            leftIcon={<IconInternalManagement />}
            open={openCustomsSubMenu}
            onToggle={toggleCustomsSubMenu}
          >
            <MenuItemLink
              to="/CustomsList"
              primaryText="Customs List"
              leftIcon={<IconSortList />}
              style={{ paddingLeft: 32 }}
            />
          </SubMenu>
        </>
      )}

      {/* Form Template */}
      {/* Form Template / Template Tool Menu */}
      {(() => {
        // Global / Region / Country branch
        if (["Global", "Region", "Country"].includes(userData.dsvRole) && userData?.isFtCrudVisible) {
          return (
            <>
              <SubMenu
                text="Form Template"
                leftIcon={<IconInternalManagement />}
                open={openFormTemplateSubMenu}
                onToggle={toggleFormTemplateSubMenu}
              >
                  <MenuItemLink
                    to="/emailtemplatelist"
                    primaryText="Email Template List"
                    leftIcon={<IconSortList />}
                    style={{ paddingLeft: 32 }}
                  />
                  <MenuItemLink
                    to="/forms/formtemplatelist"
                    primaryText="Form Templates"
                    leftIcon={<IconSortList />}
                    style={{ paddingLeft: 32 }}
                  />
                  <MenuItemLink
                    to="/forms/formallocation"
                    primaryText="Form Allocation"
                    leftIcon={<IconSortList />}
                    style={{ paddingLeft: 32 }}
                  />
                  <MenuItemLink
                    to="/forms/formsummary"
                    primaryText="Form Summary"
                    leftIcon={<IconSortList />}
                    style={{ paddingLeft: 32 }}
                  />
                  <MenuItemLink
                    to="/forms/sendlogindetails"
                    primaryText="Send Login Details"
                    leftIcon={<IconSortList />}
                    style={{ paddingLeft: 32 }}
                  />
                  <MenuItemLink
                    to="/forms/formsubmissionlist"
                    primaryText="Form Submission List"
                    leftIcon={<IconSortList />}
                    style={{ paddingLeft: 32 }}
                  />
              </SubMenu>
            </>
          );
        }
        // Exhibitor branch
        else if (userData.dsvRole === "Exhibitor" && userData?.isFtViewSubmitVisible) {
          return (
            <>
              <SubMenu
                text="Template Tool"
                leftIcon={<IconSortList />}
                open={openFormTemplateSubMenu}
                onToggle={toggleFormTemplateSubMenu}
              >
                  <MenuItemLink
                    to="/EMOExhibitorForms"
                    primaryText="Exhibitor Forms"
                    leftIcon={<IconSortList />}
                    style={{ paddingLeft: 32 }}
                  />
              </SubMenu>
            </>
          );
        }

        // fallback: nothing
        return null;
      })()}
    </Menu>
  );
};

export const Layout = () => {
  const { identity, isPending /*, error */ } = useGetIdentity();
  const navigate = useNavigate();
  const location = useLocation();
  const logout = useLogout();

  const { data: storedShowId } = useGetSelectedShowIdQuery();
  const [setSelectedShowId] = useSetSelectedShowIdMutation();
  const [selectedValue, setSelectedValue] = useState(storedShowId ?? "-1");
  const userId = localStorage.getItem("userid")
    ? parseInt(localStorage.getItem("userid")!)
    : 0;
  const userType = localStorage.getItem("userType")
    ? localStorage.getItem("userType")!
    : "";

  // Skip shows query on landing pages
  const isLandingPage = location.pathname.includes("/TrackShipments") ||
    location.pathname.includes("/MakePayment") ||
    location.pathname.includes("/SignUp") ||
    location.pathname.includes("/TrackAndTracePod");

  const { data: showsddl = [], isLoading, refetch } = useGetUserShowsForDDLQuery({
    user_id: userId
  }, {
    skip: isLandingPage
  });

  React.useEffect(() => {
    if (!isLandingPage) {
      refetch();
    }
  }, [location.pathname, isLandingPage, refetch]);
  // Disable show dropdown when on ServiceDetail edit page
  const isServiceDetailPage = location.pathname.includes(
    "/jobservice/editservice"
  );
  // Fetch shows data and bind to select list
  // Already done below: useGetUserShowsForDDLQuery is called and showsddl is mapped to <Select>
  React.useEffect(() => {
    if ((storedShowId ?? "-1") !== selectedValue) {
      setSelectedValue(storedShowId ?? "-1");
    }
  }, [storedShowId, selectedValue]);

  const handleShowChange = (event: React.SyntheticEvent, value: any) => {
    const val = value?.id || "-1";
    setSelectedValue(val);
    setSelectedShowId(val === "-1" ? null : val);
  };

  const showOptions = [
    //  { id: "-1", label: "Select All" },
    ...showsddl.map((show) => ({
      id: show.mstr_Show_id?.toString() || "",
      label: `${show.showcode || ""}${show.showcode && show.showName ? " - " : ""}${show.showName || ""}`,
    })),
  ];

  const handleLogout = async () => {
    try {
      localStorage.clear();
      sessionStorage.clear();
      // Clear all cookies
      document.cookie.split(";").forEach((c) => {
        document.cookie = c
          .replace(/^\s+/g, "")
          .replace(/=.*/, "=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/");
      });
      const commonLayoutUrl = `${window.location.origin}/`;
      const logoutParams = new URLSearchParams({
        client_id: ENV.KEYCLOAK_CLIENT_ID,
        post_logout_redirect_uri: commonLayoutUrl,
        redirect_uri: commonLayoutUrl,
      });
      const keycloakLogoutUrl = `${ENV.KEYCLOAK_URL}/realms/${ENV.KEYCLOAK_REALM}/protocol/openid-connect/logout?${logoutParams.toString()}`;
      window.location.replace(keycloakLogoutUrl);
  
    }
     catch (error) {
      console.error("Logout error:", error);
      localStorage.clear();
      sessionStorage.clear();
      document.cookie.split(";").forEach((c) => {
        document.cookie = c
          .replace(/^\s+/g, "")
          .replace(/=.*/, "=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/");
      });
      window.location.replace("/");
    }
    
  };

  return (
    <Stack
      id="core"
      sx={{
        flexDirection: "column",
        height: "100vh",
        zIndex: 1,
        position: "relative",
        width: "100%",
        overflow: "hidden",
      }}
    >
      <Stack id="app-frame" sx={{ flexDirection: "column", flexGrow: 1 }}>
        <AppBar
          userMenu={
            <>
              {!isPending && identity && (
                <>
                  <Autocomplete
                    id="topShowddl"
                    options={showOptions}
                    value={showOptions.find((opt) => opt.id === selectedValue) || null}
                    onChange={handleShowChange}
                    disabled={isServiceDetailPage}
                    loading={isLoading}
                    noOptionsText={showsddl.length === 0 ? "No shows available" : "No options"}
                    sx={{
                      width: "400px",
                      marginRight: 2,
                    }}
                    slotProps={{
                      paper: {
                        sx: {
                          maxHeight: 300,
                        },
                      },
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        size="small"
                        placeholder="Search shows..."
                        sx={{
                          backgroundColor: "white",
                          color: "black",
                          "& .MuiOutlinedInput-root": {
                            color: "black",
                            "& fieldset": {
                              borderColor: "black",
                            },
                            "&:hover fieldset": {
                              borderColor: "black",
                            },
                            "&.Mui-focused fieldset": {
                              borderColor: "black",
                            },
                          },
                          "& .MuiSvgIcon-root": { color: "black" },
                        }}
                      />
                    )}
                  />
                  <UserMenu>
                    {localStorage.getItem("userType") && localStorage.getItem("userType")!.toLowerCase() === "dsv" && (
                      <MenuItemLink
                        to={`/userlist/createuser?mstr_User_ID=${localStorage.getItem("userid") || ""}`}
                        primaryText="Edit Profile"
                        leftIcon={<IconUserManagement />}
                        style={{ minWidth: 180 }}
                      />
                    )}
                    <Logout onClick={handleLogout} />
                  </UserMenu>
                </>
              )}
              
            </>
          }
        >
          <Stack direction="row" gap={2}>
            <SidebarToggleButton />

            <Logo />
            <AppName name="efairs" />
          </Stack>
          <Box flex="1" />
        </AppBar>

        <Box component="main" sx={{ display: "flex", flexGrow: 1 }}>
          {!isPending && identity && userType != "Exhibitor" && (
            <Box component="main">
              <Sidebar alwaysOn collapsible>
                <MyMenu />
              </Sidebar>
            </Box>
          )}

          <Box
            id="main-content"
            sx={(theme) => ({
              height: "calc(100vh - 48px)",
              overflowY: "auto",
              display: "flex",
              flexDirection: "column",
              flexGrow: 1,
              flexBasis: 0,
              [theme.breakpoints.down("md")]: {
                height: "calc(100vh - 64px)",
                paddingRight: theme.spacing(4),
                paddingLeft: theme.spacing(4),
              },
            })}
          >
            <Box
              sx={(theme) => ({
                paddingRight: theme.spacing(6),
                paddingLeft: theme.spacing(6),
              })}
            >
              <Suspense fallback={"...loading"}>
                <Outlet />
              </Suspense>
            </Box>
          </Box>
        </Box>
      </Stack>
    </Stack>
  );
};
